<template>
    <m-pageframe title="Demo Gateway">
        <v-container>
            <link-back :to="{ name: 'demo-selector' }" text="All Demo Experiences" />

            <v-card class="content-card" flat>
                <v-card-text class="px-0 py-8">
                    <div class="hero-section">
                        <slot name="hero">
                            <v-icon
                                v-if="hero_icon"
                                size="60"
                                color="primary"
                                class="mb-2"
                                aria-hidden="true"
                            >
                                {{ hero_icon }}
                            </v-icon>

                            <h1 class="text-h4 font-weight-bold mb-1">{{ hero_title }}</h1>
                            <p class="text-h6 mb-4 font-weight-light">
                                {{ hero_subtitle }}
                            </p>
                        </slot>
                    </div>

                    <slot name="default">
                        <div class="demo-overview">
                            <section class="overview-grid">
                                <div
                                    v-if="business_impact_items.length > 0"
                                    class="overview-section"
                                >
                                    <h3 class="text-h5 mb-3">Business Impact</h3>
                                    <div class="benefit-list">
                                        <div
                                            v-for="benefit in business_impact_items"
                                            :key="benefit.title"
                                            class="benefit-item"
                                        >
                                            <v-icon
                                                color="primary"
                                                class="benefit-icon"
                                                aria-hidden="true"
                                            >{{ benefit.icon }}</v-icon>
                                            <div>
                                                <strong>{{ benefit.title }}</strong><br>
                                                <span>{{ benefit.description }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
    
                                <div
                                    v-if="experience_flow_steps.length > 0"
                                    class="overview-section"
                                >
                                    <h3 class="text-h5 mb-3">Experience Flow</h3>
                                    <div class="flow-steps">
                                        <div
                                            v-for="step in experience_flow_steps"
                                            :key="step.number"
                                            class="flow-step"
                                        >
                                            <div class="step-number">{{ step.number }}</div>
                                            <div class="step-content">
                                                <strong>{{ step.title }}</strong><br>
                                                <span>{{ step.description }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
    
                            <section
                                v-if="compliance_items.length > 0"
                                class="compliance-section"
                            >
                                <h3 class="text-h5 mb-3">Regulatory Compliance</h3>
                                <div class="compliance-grid">
                                    <div
                                        v-for="item in compliance_items"
                                        :key="item.title"
                                        class="compliance-item"
                                    >
                                        <v-icon
                                            color="secondary"
                                            size="32"
                                            aria-hidden="true"
                                        >{{ item.icon }}</v-icon>
                                        <div>
                                            <strong>{{ item.title }}</strong><br>
                                            <span v-html="item.description"></span>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </div>
    
                        <div class="text-center">
                            <v-btn
                                color="primary"
                                large
                                @click="handleStartDemo"
                            >
                                <v-icon
                                    size="24"
                                    aria-hidden="true"
                                    left
                                >
                                    mdi-play
                                </v-icon>
                                Start Customer Experience Demo
                            </v-btn>
                        </div>
                    </slot>
                </v-card-text>
            </v-card>
        </v-container>
    </m-pageframe>
</template>

<script>
    export default {
        name: 'DemoGatewayFrame',

        props: {
            demo_route_name: {
                type: String,
                required: true
            },
            hero_icon: {
                type: String,
                default: ''
            },
            hero_title: {
                type: String,
                default: ''
            },
            hero_subtitle: {
                type: String,
                default: ''
            },
            business_impact_items: {
                type: Array,
                default: () => []
            },
            experience_flow_steps: {
                type: Array,
                default: () => []
            },
            compliance_items: {
                type: Array,
                default: () => []
            },
        },

        methods: {
            handleStartDemo() {
                const route = this.$router.resolve({ name: this.demo_route_name });
                window.open(route.href, '_blank');
            },
        }
    }
</script>

<style lang="scss" scoped>
.container {
    max-width: 1200px;
    margin: 0 auto 5rem auto;

    .content-card {
        width: 100%;
        text-align: center;
    }

    .hero-section {
        margin-bottom: 2.5rem;
    }
}

.demo-overview {
    text-align: left;

    .overview-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 3rem;
        margin-bottom: 3rem;

        @media (max-width: 900px) {
            grid-template-columns: 1fr;
            gap: 2rem;
        }
    }

    .overview-section {
        .benefit-list {
            display: flex;
            flex-direction: column;
            gap: 1.5rem;

            .benefit-item {
                display: flex;
                align-items: flex-start;
                gap: 1rem;

                .benefit-icon {
                    font-size: 1.5rem !important;
                    margin-top: 0.25rem;
                }

                div {
                    line-height: 1.4;

                    strong {
                        color: var(--v-primary-base);
                        font-size: 1.1rem;
                    }

                    span {
                        color: #666;
                        font-size: 0.95rem;
                    }
                }
            }
        }

        .flow-steps {
            display: flex;
            flex-direction: column;
            gap: 1.5rem;

            .flow-step {
                display: flex;
                align-items: center;
                gap: 1rem;

                .step-number {
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    background: var(--v-primary-base);
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: bold;
                    flex-shrink: 0;
                }

                .step-content {
                    line-height: 1.4;

                    strong {
                        color: var(--v-primary-base);
                        font-size: 1.1rem;
                    }

                    span {
                        color: #666;
                        font-size: 0.95rem;
                    }
                }
            }
        }
    }
}

.compliance-section {
    background: var(--color-bg-shaded-light);
    border-radius: 0.75rem;
    padding: 1.5rem 2rem 2rem 2rem;
    margin-bottom: 3rem;

    .compliance-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 2rem;
        margin-top: 1rem;

        .compliance-item {
            --v-anchor-base: var(--v-secondary-base);

            display: flex;
            align-items: flex-start;
            gap: 1rem;

            div {
                line-height: 1.4;

                strong {
                    color: var(--v-secondary-base);
                    font-size: 1.1rem;
                }

                span {
                    color: #666;
                    font-size: 0.95rem;
                }
            }
        }
    }
}
</style>